import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { MouseEvent, PointerEvent, TouchEvent } from 'react'
import { createHold, createSwipe, type HoldOptions, type PointerSample, type SwipeOptions } from './recognisers'

/** Gestures never start on form fields, media controls, dialogs or anything marked data-no-gestures. */
export const NO_GESTURES = 'input, textarea, select, video, audio, dialog, [data-no-gestures]'

/** A click this soon after a swipe or hold belongs to the gesture, not to what was under the finger. */
const CLICK_AFTER_GESTURE_MS = 400

export interface PointerGestureOptions {
  swipe?: SwipeOptions | null
  hold?: HoldOptions | null
}

interface Session {
  pointerId: number
  element: HTMLElement
  swipe: ReturnType<typeof createSwipe> | null
  hold: ReturnType<typeof createHold> | null
}

const recognisersOf = (session: Session) => [session.swipe, session.hold].filter((r) => r !== null)

/** A press on the element: what pressed, and which button (0 for a finger or a pen tip). */
export interface Press {
  pointerType: string
  button: number
}

/**
 * Fingers swipe and hold; pens (an Apple Pencil) only hold, for the sheet: they write and select too. A pen's barrel
 * button is a right-click, whose menu may only come after the pen lifts (Windows), so it is not a hold.
 */
export const pressHolds = (press: Press | null) =>
  press !== null && press.button === 0 && (press.pointerType === 'touch' || press.pointerType === 'pen')

const sample = (event: PointerEvent): PointerSample => ({
  pointerId: event.pointerId,
  clientX: event.clientX,
  clientY: event.clientY,
  timeStamp: event.timeStamp,
})

/**
 * Touch swipe and hold on an element (spec §3.1): spread `handlers` on it and give the element
 * `touch-action: pan-y` so vertical scrolling stays with the browser. Pens hold but don't swipe, and the
 * system's long-press menu counts as the hold. Mice, and a pen's barrel button, use clicks and right-click instead.
 * The latest options are used, so callbacks need not be stable.
 */
export function usePointerGestures(options: PointerGestureOptions) {
  const optionsRef = useRef(options)
  const session = useRef<Session | null>(null)
  const lastPress = useRef<Press | null>(null)
  /** The current or last touch became a swipe or hold: its touchend and click are not a tap. */
  const recognised = useRef(false)
  const clickBlockedUntil = useRef(0)

  useEffect(() => {
    optionsRef.current = options
    // The element no longer takes a gesture in progress (e.g. its message was deleted): end it where it is.
    if (!options.swipe) session.current?.swipe?.cancel()
    if (!options.hold) session.current?.hold?.cancel()
  })

  const cancel = useCallback(() => {
    if (session.current) recognisersOf(session.current).forEach((r) => r.cancel())
    session.current = null
  }, [])

  useEffect(() => cancel, [cancel])

  const handlers = useMemo(() => {
    const own = (event: { currentTarget: Element; target: EventTarget }) => event.currentTarget.contains(event.target as Node)

    return {
      onPointerDown(event: PointerEvent<HTMLElement>) {
        // Events from portals (a sheet opened from this element) bubble here through React: not ours.
        if (!own(event)) return
        lastPress.current = { pointerType: event.pointerType, button: event.button }
        if (!pressHolds(lastPress.current)) return
        recognised.current = false
        if (!event.isPrimary) {
          // A second finger: zooming, not a gesture on this element.
          cancel()
          return
        }
        // A new first finger: whatever was tracked before has ended out of sight (e.g. captured elsewhere).
        cancel()
        if ((event.target as Element).closest(NO_GESTURES)) return

        const hold = optionsRef.current.hold
        const swipe = event.pointerType === 'touch' ? optionsRef.current.swipe : null
        if (!swipe && !hold) return
        const current: Session = { pointerId: event.pointerId, element: event.currentTarget, swipe: null, hold: null }
        // A swipe and a hold can both begin with the same press; whichever is recognised first stops the other.
        // Callbacks use the latest options, or the ones the gesture started with once those are gone.
        if (swipe) {
          current.swipe = createSwipe({
            ...swipe,
            onStart: () => {
              recognised.current = true
              current.hold?.cancel()
              try {
                // Keep receiving the finger's movement when it leaves the element.
                current.element.setPointerCapture?.(current.pointerId)
              } catch {
                // The pointer is already gone.
              }
              optionsRef.current.swipe?.onStart?.()
            },
            onMove: (state) => (optionsRef.current.swipe ?? swipe).onMove(state),
            onEnd: (result) => (optionsRef.current.swipe ?? swipe).onEnd(result),
          })
        }
        if (hold) {
          current.hold = createHold({
            ...hold,
            onHold: (start) => {
              // Set now: what the hold opens may cover the finger, so its pointerup may never come here.
              recognised.current = true
              current.swipe?.cancel()
              optionsRef.current.hold?.onHold(start)
            },
          })
        }
        session.current = current
        recognisersOf(current).forEach((r) => r.down(sample(event)))
      },
      // Move, up and cancel follow the tracked finger wherever its events land: a sheet the hold opened covers it.
      onPointerMove(event: PointerEvent<HTMLElement>) {
        if (session.current?.pointerId !== event.pointerId) return
        recognisersOf(session.current).forEach((r) => r.move(sample(event)))
      },
      onPointerUp(event: PointerEvent<HTMLElement>) {
        const current = session.current
        if (current?.pointerId !== event.pointerId) return
        recognisersOf(current).forEach((r) => r.up(sample(event)))
        session.current = null
        if (recognised.current) clickBlockedUntil.current = Date.now() + CLICK_AFTER_GESTURE_MS
      },
      onPointerCancel(event: PointerEvent<HTMLElement>) {
        if (session.current?.pointerId === event.pointerId) cancel()
      },
      onContextMenu(event: MouseEvent<HTMLElement>) {
        if (!own(event) || !pressHolds(lastPress.current)) return
        // Android opens its long-press menu (at about 400 ms) before the hold fires: that is the hold, early.
        if (session.current?.hold?.fire() || recognised.current) event.preventDefault()
      },
      onTouchEnd(event: TouchEvent<HTMLElement>) {
        // No click or compatibility mouse events after a swipe or hold. A touchend goes to where the touch started,
        // so taps in a sheet the hold opened (a portal: its events bubble here too) are left alone.
        if (recognised.current && own(event) && event.cancelable) event.preventDefault()
      },
      onClickCapture(event: MouseEvent<HTMLElement>) {
        if (Date.now() < clickBlockedUntil.current && own(event)) {
          event.preventDefault()
          event.stopPropagation()
        }
      },
    }
  }, [cancel])

  return {
    handlers,
    /** The last press on the element: tells a touch long press from a right-click (see pressHolds). */
    lastPress,
  }
}
